/**
 * 크레딧·크레딧 관리 — 관리자 API 클라이언트
 *
 *  BE 엔드포인트(/api/admin/credit/*) 와 1:1 매핑.
 *  타입은 BE DTO(AdminCreditDto.*) 와 키 이름까지 정합.
 *  컴포넌트는 이 모듈의 hooks 를 호출하고, mock 시절과 시그니처 동일.
 */
import { apiClient } from './client';
import { API_ENDPOINTS } from '../../config/api';

/* ─────────────────────────────────────────────────────────────────
 *  타입 정의 — BE DTO 와 1:1 매핑
 * ───────────────────────────────────────────────────────────────── */

/**
 * 트랜잭션 타입 코드 (사장님 액션·관리자 액션 통합).
 *
 *  DB(INFO_CREDIT_PRICE.CD_TX_TYPE / HIST_COMPANY_CREDIT_TX.CD_TX_TYPE)는
 *  VARCHAR(40) 자유 문자열 — ENUM 이 아니므로 관리자 화면에서 신규 코드를
 *  동적으로 등록할 수 있다. 따라서 타입은 strict union 이 아닌 `string` 으로 둔다.
 *
 *  KNOWN_CREDIT_TX_TYPES 는 "현재 코드/시드에 알려진" 표준 코드 목록으로,
 *  자동완성·라벨 매핑 등 UI 보조용으로만 사용. 코드 분기 로직은 prefix
 *  ('USE_*' 등) 또는 KNOWN 목록 포함 여부로 판단.
 */
export type CdCreditTxType = string;

/** 알려진 표준 코드 — 시드 + 코드 베이스에서 사용 중인 코드. UI 자동완성/라벨용. */
export const KNOWN_CREDIT_TX_TYPES = [
  'GRANT_SIGNUP',
  'GRANT_MONTHLY_FREE',
  'GRANT_PROMOTION',
  'GRANT_REFERRAL',
  'GRANT_PAYMENT_REWARD',
  'GRANT_OPERATION',
  'GRANT_BUG_COMPENSATION',
  'PURCHASE',
  'PURCHASE_MANUAL',
  'PURCHASE_FROM_SETTLEMENT',
  'REFUND_PAYMENT_FEE',
  'ADMIN_ADJUSTMENT',
  'REVERSAL',
  'USE_AI_DIARY_TEXT',
  'USE_AI_DIARY_PHOTO',
  'USE_AI_DIARY_VIDEO',
  'USE_AI_DIARY_REGEN',
  'USE_TICKET_OFFLINE',
  'USE_SMS',
  'USE_KAKAO_ALIM',
  'USE_SMS_FALLBACK',
  'USE_CONTRACT_PUSH',
  'USE_PHOTO_BACKUP',
  'EXPIRE_FREE',
  'REFUND_TO_USER',
] as const;

/** 자금 출처 대분류 (CREDIT 방향만 의미). */
export type CdFundSource =
  | 'PG_PAYMENT'
  | 'SETTLEMENT_CONVERT'
  | 'ADMIN_GRANT'
  | 'SYSTEM_GRANT'
  | 'PROMOTION'
  | 'REVERSAL_RESTORE';

/** 관리자 지급 카테고리. */
export type CreditGrantCategory = 'OPERATION' | 'BUG_COMP' | 'PURCHASE_MANUAL';

/** 만료 설정 모드. */
export type ExpireMode = 'DEFAULT' | 'DAYS' | 'DATE' | 'NEVER';

export type CdCreditPlan = 'PLAN_FREE' | 'PLAN_BASIC' | 'PLAN_PRO' | 'PLAN_BUSINESS';

/** 사장님에게 보여줄 한글 라벨 매핑. */
export const CREDIT_TX_LABEL: Record<string, string> = {
  GRANT_SIGNUP: '신규 가입 지급',
  GRANT_MONTHLY_FREE: '매월 무료 지급',
  GRANT_PROMOTION: '프로모션 지급',
  GRANT_REFERRAL: '추천 가입 보상',
  GRANT_PAYMENT_REWARD: '결제 보상',
  GRANT_OPERATION: '이벤트 보너스',
  GRANT_BUG_COMPENSATION: '장애 보상',
  PURCHASE: '크레딧 구매',
  PURCHASE_MANUAL: '결제 충전 (사후 반영)',
  PURCHASE_FROM_SETTLEMENT: '정산 전환 충전',
  REFUND_PAYMENT_FEE: '결제 수수료 환급',
  ADMIN_ADJUSTMENT: '관리자 수동 조정',
  REVERSAL: '트랜잭션 환원',
  USE_AI_DIARY_TEXT: 'AI 알림장 텍스트 다듬기',
  USE_AI_DIARY_IMAGE: 'AI 알림장 (이미지 분석)',
  USE_AI_DIARY_PHOTO: 'AI 알림장 (사진)',
  USE_AI_DIARY_VIDEO: 'AI 알림장 (동영상)',
  USE_AI_DIARY_REGEN: 'AI 알림장 재생성',
  USE_TICKET_OFFLINE: '이용권 수동 발급',
  USE_SMS: 'SMS 발송',
  USE_KAKAO_ALIM: '알림톡 발송',
  USE_SMS_FALLBACK: '대체문자(SMS) 발송',
  USE_CONTRACT_PUSH: '계약서 푸시',
  USE_PHOTO_BACKUP: '사진 백업',
  EXPIRE_FREE: '무료분 만료',
  REFUND_TO_USER: '사장님 환불',
};

/** 자금 출처별 표시 메타 (아이콘 + 라벨). */
export const FUND_SOURCE_META: Record<CdFundSource, { label: string; emoji: string }> = {
  PG_PAYMENT:         { label: 'PG 결제',     emoji: '💳' },
  SETTLEMENT_CONVERT: { label: '정산 전환',   emoji: '🔄' },
  ADMIN_GRANT:        { label: '관리자 지급', emoji: '🛠' },
  SYSTEM_GRANT:       { label: '시스템 지급', emoji: '🎁' },
  PROMOTION:          { label: '프로모션',    emoji: '🎉' },
  REVERSAL_RESTORE:   { label: '환원 복원',   emoji: '↶' },
};

/** 카테고리별 표시 메타. */
export const GRANT_CATEGORY_META: Record<CreditGrantCategory, { label: string; desc: string; pool: 'FREE' | 'PAID'; expireSupported: boolean; emoji: string }> = {
  OPERATION:       { label: '운영 무료 발급', desc: '이벤트·마케팅·보너스·장애 보상 등 (만료 모드로 구분)', pool: 'FREE', expireSupported: true,  emoji: '🎉' },
  BUG_COMP:        { label: '장애/버그 보상', desc: '시스템 오류 보상 (만료 없음) — deprecated: OPERATION 으로 통합', pool: 'FREE', expireSupported: false, emoji: '🛠' },
  PURCHASE_MANUAL: { label: 'PG 결제 누락 보정', desc: '사장님 결제분 사후 반영 (환불 의무)', pool: 'PAID', expireSupported: false, emoji: '💳' },
};

/** UI 에서 노출할 카테고리만 추린 목록 (BUG_COMP 는 OPERATION 으로 흡수). */
export const VISIBLE_GRANT_CATEGORIES: CreditGrantCategory[] = ['OPERATION', 'PURCHASE_MANUAL'];

/** USE_ 로 시작하는 차감 타입 판정 (단가 화면 필터링용). */
export const isDebitType = (t: string): boolean => t.startsWith('USE_');

/** 전체 환원 결과. */
export interface BulkReverseResult {
  attemptedCount: number;
  successCount: number;
  skippedCount: number;
  restoredCredits: number;   // DEBIT 원본 환원으로 복원된 합
  recoveredCredits: number;  // CREDIT 원본 환원으로 회수된 합
  wallet: CompanyWallet | null;
}

/** 사업장 지갑 응답. */
export interface CompanyWallet {
  companySeq: number;
  companyName?: string;
  region?: string;
  bizUserName?: string;
  balance: number;
  freeBalance: number;
  paidBalance: number;
  monthlyUsed: number;
  monthlyPurchased: number;
  lifetimeGranted: number;
  lifetimeUsed: number;
  dtFreeExpire?: string | null;
  dtLastTx?: string | null;
  cdPlan?: string;
  exemptUntil?: string | null;
  exemptReason?: string | null;
  /** 활성 OWNER 사장님 보유 여부 — false 면 '삭제된 사업자' 배지 표시. */
  ownerActive?: boolean | null;
}

/** 사업장 단건 + 트랜잭션 상세 응답. */
export interface CompanyWalletDetail {
  wallet: CompanyWallet;
  transactions: CompanyTxItem[];
}

/** 사업장 트랜잭션 1행. */
export interface CompanyTxItem {
  txSeq: number;
  companySeq: number;
  cdTxType: CdCreditTxType;
  cdFundSource?: CdFundSource | null;
  cdDirection: 'CREDIT' | 'DEBIT';
  amount: number;
  balanceAfter: number;
  sourceType?: string;
  sourceSeq?: number;
  adminSeq?: number;
  adminName?: string;
  reason?: string;
  description: string;
  dtRegist: string;
  // Lot 메타
  dtExpire?: string | null;
  remainingAmt?: number | null;
  expireStatus?: 'ACTIVE' | 'EXPIRED' | 'EXHAUSTED';
  // 환원
  reversalOfTxSeq?: number | null;
  reversed?: boolean;
  // 카테고리 메타
  grantCategory?: CreditGrantCategory | null;
  campaignName?: string | null;
  incidentId?: string | null;
  impactPeriod?: string | null;
  pgTxnId?: string | null;
  purchasePriceKrw?: number | null;
  packageSeq?: number | null;
}

/** 지급 요청 페이로드 — 카테고리별로 필수 필드가 다름. */
export interface GrantRequest {
  amount: number;
  reason: string;
  grantCategory: CreditGrantCategory;
  // OPERATION
  campaignName?: string;
  // BUG_COMP
  incidentId?: string;
  impactPeriod?: string;
  // PURCHASE_MANUAL
  pgTxnId?: string;
  purchasePriceKrw?: number;
  purchaseDt?: string;     // ISO datetime
  packageSeq?: number;
  // 만료 설정 (OPERATION 만 의미)
  expireMode?: ExpireMode;
  expireDays?: number;
  expireDate?: string;     // YYYY-MM-DD
}

export interface CreditPrice {
  priceSeq: number;
  cdTxType: CdCreditTxType;
  unitPrice: number;
  description?: string;
  dtEffectiveFrom: string;
  dtEffectiveTo?: string | null;
  dtUpdate: string;
  dtRegist: string;
  delYn: 'Y' | 'N';
}

export interface CreditPolicy {
  policySeq: number;
  policyKey: string;
  policyValue: string;
  cdDataType: 'INT' | 'STRING' | 'BOOLEAN' | 'JSON' | 'DECIMAL';
  description?: string;
  dtUpdate: string;
}

export interface CreditPackage {
  packageSeq: number;
  packageName: string;
  amount: number;
  priceKrw: number;
  discountRate?: number | null;
  dataOrder: number;
  activeYn: 'Y' | 'N';
  dtVisibleFrom?: string | null;
  dtVisibleTo?: string | null;
  dtUpdate: string;
  dtRegist: string;
}

export interface CreditPlan {
  planSeq: number;
  cdPlan: CdCreditPlan;
  planName: string;
  monthlyPriceKrw: number;
  monthlyQuota?: number | null;
  featuresJson?: string;
  dataOrder: number;
  activeYn: 'Y' | 'N';
}

export interface CreditPolicyChange {
  changeSeq: number;
  adminSeq: number;
  adminName?: string;
  targetType: 'PRICE' | 'POLICY' | 'PACKAGE' | 'PLAN' | 'COMPANY';
  targetSeq?: number;
  targetLabel: string;
  action: 'CREATE' | 'UPDATE' | 'DEACTIVATE';
  reason: string;
  dtRegist: string;
}

export interface CreditStats {
  periodFrom: string;
  periodTo: string;
  creditsGranted: number;
  creditsUsed: number;
  creditsRefunded: number;
  creditsPurchased: number;
  purchaseRevenueKrw: number;
  paymentFeeRevenueKrw: number;
  byCategory: Array<{
    cdTxType: CdCreditTxType;
    label: string;
    count: number;
    amount: number;
  }>;
  topCompanies: Array<{
    companySeq: number;
    companyName: string;
    creditsUsed: number;
    creditsPurchased: number;
  }>;
}

export interface BulkGrantResult {
  totalRows: number;
  successCount: number;
  failedRows: Array<{ companySeq: number; error: string }>;
  totalCreditsGranted: number;
}

/* ─────────────────────────────────────────────────────────────────
 *  HTTP 헬퍼 — BaseResponse 에서 data 추출
 * ───────────────────────────────────────────────────────────────── */

/** BaseResponse 응답에서 data 만 안전하게 꺼냄 (래퍼 유무에 모두 대응). */
const unwrap = <T,>(resp: { data?: any }): T => {
  if (resp?.data?.data !== undefined) return resp.data.data as T;
  return resp?.data as T;
};

export interface CompanyCreditUsage {
  balance: number;
  debit30: number;
  debit90: number;
  avgDaily30: number;
  avgDaily90: number;
  projectedDays30: number | null;
  projectedDays90: number | null;
  rechargeCount: number;
  avgRechargeIntervalDays: number | null;
  lastRechargeAt: string | null;
  lastRechargeAmount: number | null;
  estRechargeCycleDays: number | null;
}

const E = API_ENDPOINTS.ADMIN_CREDIT;

/* ─────────────────────────────────────────────────────────────────
 *  API 모듈
 * ───────────────────────────────────────────────────────────────── */

export const creditApi = {
  // ─── 통계 ──
  async getStats(): Promise<CreditStats> {
    return unwrap<CreditStats>(await apiClient.get(E.STATS));
  },

  // ─── 사업장 지갑 ──
  async listCompanyWallets(params: { q?: string; sort?: string; sortDir?: 'asc' | 'desc' } = {}): Promise<CompanyWallet[]> {
    return unwrap<CompanyWallet[]>(await apiClient.get(E.COMPANY_LIST, { params }));
  },

  async getCompanyWallet(
    companySeq: number,
    from?: string,
    to?: string,
  ): Promise<CompanyWalletDetail> {
    const params = from && to ? { from, to } : undefined;
    return unwrap<CompanyWalletDetail>(
      await apiClient.get(E.COMPANY_DETAIL(companySeq), { params }),
    );
  },

  /** 사업장 크레딧 소모율/소진예상/충전주기 */
  async getCompanyCreditUsage(companySeq: number): Promise<CompanyCreditUsage> {
    return unwrap<CompanyCreditUsage>(await apiClient.get(E.COMPANY_USAGE(companySeq)));
  },

  /**
   * 사업장 크레딧 수동 지급.
   *  카테고리(OPERATION/BUG_COMP/PURCHASE_MANUAL)에 따라 잔액 풀과 만료가 자동 결정됨.
   *  카테고리별 필수 필드는 BE 에서 검증.
   */
  async grantToCompany(companySeq: number, payload: GrantRequest): Promise<CompanyWallet> {
    return unwrap<CompanyWallet>(
      await apiClient.post(E.COMPANY_GRANT(companySeq), payload)
    );
  },

  /** @deprecated 임의 차감은 권장되지 않음 — 잘못된 지급은 reverseTx 사용. */
  async deductFromCompany(companySeq: number, amount: number, reason: string): Promise<CompanyWallet> {
    return unwrap<CompanyWallet>(
      await apiClient.post(E.COMPANY_DEDUCT(companySeq), { amount, reason })
    );
  },

  /**
   * 트랜잭션 환원 — 원본 TX 를 되돌림.
   *  멱등 (한 번만 가능, 중복 호출 시 BE 가 거부).
   *  원본이 차감이면 잔액 복원, 원본이 지급이면 잔액 회수 (사용된 분 제외).
   */
  async reverseTx(txSeq: number, reason: string): Promise<CompanyWallet> {
    return unwrap<CompanyWallet>(
      await apiClient.post(E.TX_REVERSE(txSeq), { reason })
    );
  },

  /** 한 사업장의 환원 가능한 모든 TX 일괄 환원 (단일 트랜잭션). */
  async bulkReverseCompany(companySeq: number, reason: string): Promise<BulkReverseResult> {
    return unwrap<BulkReverseResult>(
      await apiClient.post(E.COMPANY_BULK_REVERSE(companySeq), { reason })
    );
  },

  async changeCompanyPlan(
    companySeq: number,
    cdPlan: CdCreditPlan,
    reason: string,
    feeOverride?: 'NORMAL' | 'WAIVE_TRIAL' | 'PROMO'
  ): Promise<CompanyWallet> {
    return unwrap<CompanyWallet>(
      await apiClient.post(E.COMPANY_PLAN(companySeq), { cdPlan, reason, feeOverride })
    );
  },

  async setCompanyExemption(
    companySeq: number,
    untilDate: string | null,
    reason: string
  ): Promise<CompanyWallet> {
    return unwrap<CompanyWallet>(
      await apiClient.post(E.COMPANY_EXEMPT(companySeq), { untilDate, reason })
    );
  },

  async bulkGrant(
    rows: Array<{ companySeq: number; amount: number }>,
    grantType: 'FREE' | 'PAID',
    reason: string
  ): Promise<BulkGrantResult> {
    return unwrap<BulkGrantResult>(
      await apiClient.post(E.BULK_GRANT, { rows, grantType, reason })
    );
  },

  // ─── 단가 ──
  async listPrices(): Promise<CreditPrice[]> {
    return unwrap<CreditPrice[]>(await apiClient.get(E.PRICE_LIST));
  },

  async updatePrice(
    priceSeq: number,
    patch: { unitPrice?: number; description?: string; dtEffectiveFrom?: string },
    reason?: string
  ): Promise<CreditPrice> {
    return unwrap<CreditPrice>(
      await apiClient.patch(E.PRICE_UPDATE(priceSeq), { ...patch, reason })
    );
  },

  /** 지금까지 등록된 모든 액션 코드 (자동완성용). */
  async listKnownTxTypes(): Promise<string[]> {
    return unwrap<string[]>(await apiClient.get(E.PRICE_KNOWN_TYPES));
  },

  /** 사전 정의 안 된 USE_* 액션 단가 신규 등록. */
  async createPrice(
    input: {
      cdTxType: CdCreditTxType;
      unitPrice: number;
      description?: string;
      dtEffectiveFrom?: string;
    },
    reason?: string
  ): Promise<CreditPrice> {
    return unwrap<CreditPrice>(
      await apiClient.post(E.PRICE_CREATE, { ...input, reason })
    );
  },

  // ─── 정책 ──
  async listPolicies(): Promise<CreditPolicy[]> {
    return unwrap<CreditPolicy[]>(await apiClient.get(E.POLICY_LIST));
  },

  async updatePolicy(policyKey: string, policyValue: string, reason: string): Promise<CreditPolicy> {
    return unwrap<CreditPolicy>(
      await apiClient.patch(E.POLICY_UPDATE(policyKey), { policyValue, reason })
    );
  },

  // ─── 패키지 ──
  async listPackages(): Promise<CreditPackage[]> {
    return unwrap<CreditPackage[]>(await apiClient.get(E.PACKAGE_LIST));
  },

  async createPackage(
    input: Omit<CreditPackage, 'packageSeq' | 'dtUpdate' | 'dtRegist'>,
    reason: string
  ): Promise<CreditPackage> {
    return unwrap<CreditPackage>(
      await apiClient.post(E.PACKAGE_CREATE, { ...input, reason })
    );
  },

  async updatePackage(
    packageSeq: number,
    patch: Partial<CreditPackage>,
    reason: string
  ): Promise<CreditPackage> {
    return unwrap<CreditPackage>(
      await apiClient.patch(E.PACKAGE_UPDATE(packageSeq), { ...patch, reason })
    );
  },

  async deactivatePackage(packageSeq: number, reason?: string): Promise<void> {
    const params = reason && reason.trim() ? { reason } : undefined;
    await apiClient.delete(E.PACKAGE_DEACTIVATE(packageSeq), { params });
  },

  async activatePackage(packageSeq: number, reason?: string): Promise<void> {
    const params = reason && reason.trim() ? { reason } : undefined;
    await apiClient.post(E.PACKAGE_ACTIVATE(packageSeq), null, { params });
  },

  /** 드래그앤드롭 정렬 — 첫 위치→마지막 위치 순서대로 packageSeq 배열. */
  async reorderPackages(orderedSeqs: number[]): Promise<void> {
    await apiClient.post(E.PACKAGE_REORDER, orderedSeqs);
  },

  // ─── 정액제 플랜 ──
  async listPlans(): Promise<CreditPlan[]> {
    return unwrap<CreditPlan[]>(await apiClient.get(E.PLAN_LIST));
  },

  // ─── 변경 이력 ──
  async listChanges(limit = 50): Promise<CreditPolicyChange[]> {
    return unwrap<CreditPolicyChange[]>(await apiClient.get(E.CHANGE_LIST, { params: { limit } }));
  },
};
